import React, { useState } from 'react';

import { useSelector } from 'react-redux';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionDetails, AccordionSummary, Box, Chip, Link, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import type { SxProps, Theme } from '@mui/material/styles';

import TenantBrandChip from '~/components/Common/Elements/TenantBrandChip';

import { SAME_PHONE_ACCOUNTS_BACKEND_LIMIT, useSamePhoneAccounts } from '~/hooks/apis/users/useSamePhoneAccounts';

import { getUserTenant } from '~/utils/userIdentity';

const MAX_INITIAL_RESULTS = 10;
const LOCAL_STORAGE_KEY = 'admin.userDetails.samePhoneAccounts.expanded';
const LABEL_COLUMN_WIDTH = 130;

const getInitialExpandedState = () => {
  if (typeof window === 'undefined') {
    return false;
  }

  return window.localStorage.getItem(LOCAL_STORAGE_KEY) === 'true';
};

const setStoredExpandedState = (isExpanded: boolean) => {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.setItem(LOCAL_STORAGE_KEY, String(isExpanded));
};

interface Props {
  user: App.User;
  sx?: SxProps<Theme>;
}

export default function SamePhoneAccounts({ user, sx = [] }: Props) {
  const tenant = useSelector((state: App.State) => state.tenant);
  const [showAll, setShowAll] = useState(false);
  const [isExpanded, setIsExpanded] = useState(getInitialExpandedState);

  const { data: accounts = [], isLoading, error } = useSamePhoneAccounts(user, tenant.brand);

  const handleExpandedChange = (_: React.SyntheticEvent, expanded: boolean) => {
    setIsExpanded(expanded);
    setStoredExpandedState(expanded);
  };

  if (!user.phone) {
    return null;
  }

  if (isLoading) {
    return null;
  }

  if (error) {
    return (
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" gutterBottom>
          Same Phone Accounts
        </Typography>
        <Typography variant="body2" color="error">
          Error loading similar accounts: {error.message}
        </Typography>
      </Box>
    );
  }

  if (accounts.length === 0) {
    return null;
  }

  const displayedAccounts =
    showAll || accounts.length <= MAX_INITIAL_RESULTS ? accounts : accounts.slice(0, MAX_INITIAL_RESULTS);
  const hasMoreResults = accounts.length > MAX_INITIAL_RESULTS;
  const isAtLimit = accounts.length === SAME_PHONE_ACCOUNTS_BACKEND_LIMIT;
  const accountCountText = `${isAtLimit ? 'At least ' : ''}other account${
    accounts.length !== 1 ? 's' : ''
  } found with this phone number${
    isAtLimit ? ' (we cannot fetch more than 50 at a time, there could be more...)' : ''
  }`;
  const accountList = (
    <Box component="ul" sx={{ pl: 2, m: 0 }}>
      {displayedAccounts.map((account) => {
        const tenant = getUserTenant(account);

        return (
          <Box component="li" key={account.id_member} sx={{ mb: 1 }}>
            <Typography variant="body2" component="div" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Link
                href={`/users/${account.id_member}`}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ textDecoration: 'none' }}
              >
                Account
              </Link>
              {'\u00A0 | \u00A0'}
              <Link
                href={`/purchases/section/allOrders?customer_id=${account.id_member}`}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ textDecoration: 'none' }}
              >
                Purchases
              </Link>
              {'\u00A0 | \u00A0'}
              <Link
                href={`/users/${account.id_member}/history`}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ textDecoration: 'none' }}
              >
                History
              </Link>
              {' \u00A0\u00A0'}
              <strong>{account.full_name}</strong> ({account.email}){tenant && <TenantBrandChip tenantValue={tenant} />}
              {account.shadow_ban_user && <Chip label="Shadow banned" color="error" size="small" />}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
  const showMoreLink = hasMoreResults && (
    <Box sx={{ mt: 2 }}>
      <Link component="button" variant="body2" onClick={() => setShowAll(!showAll)} sx={{ cursor: 'pointer' }}>
        {showAll ? 'Hide' : `Show ${accounts.length} similar accounts`}
      </Link>
    </Box>
  );

  return (
    <Accordion
      expanded={isExpanded}
      onChange={handleExpandedChange}
      disableGutters
      variant="outlined"
      sx={[{ mb: 4, borderRadius: 1, '&:before': { display: 'none' } }, ...(Array.isArray(sx) ? sx : [sx])]}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: { xs: 2.5, md: 3.5 } }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: `${LABEL_COLUMN_WIDTH}px minmax(0, 1fr)` },
            gap: { xs: 1, md: 2 },
            alignItems: 'center',
            width: '100%',
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600, textAlign: { xs: 'left', md: 'center' } }}>
            Similar Accounts
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
            <Box
              component="span"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: 24,
                height: 24,
                px: 0.75,
                borderRadius: 999,
                bgcolor: (theme) => alpha(theme.palette.warning.main, 0.18),
                color: 'warning.dark',
                fontSize: 13,
                fontWeight: 700,
                lineHeight: 1,
              }}
            >
              {accounts.length}
            </Box>
            <Typography variant="body2" color="text.secondary">
              {accountCountText}
            </Typography>
          </Box>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ px: { xs: 2.5, md: 3.5 }, pt: 0 }}>
        {accountList}
        {showMoreLink}
      </AccordionDetails>
    </Accordion>
  );
}
